import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { BhutanMap } from "./BhutanMap";
import "./IterinaryNavs.css";
import { navItems, regions, regionData, slugify } from "../../data/tours";

interface ItineraryNavsProps {
  initialCategory?: string;
}

export default function ItineraryNavs({ initialCategory }: ItineraryNavsProps) {
  const [activeNav, setActiveNav] = useState(
    initialCategory && navItems.includes(initialCategory)
      ? initialCategory
      : navItems[0]
  );
  const [activeRegion, setActiveRegion] = useState("Paro");
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const toursRef = useRef<HTMLDivElement>(null);

  const checkScroll = () => {
    const container = toursRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth
      );
    }
  };

  const scrollTours = (direction: "left" | "right") => {
    const container = toursRef.current;
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === "right" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    checkScroll();
    const container = toursRef.current;
    if (container) container.addEventListener("scroll", checkScroll);
    return () => container?.removeEventListener("scroll", checkScroll);
  }, [activeRegion, activeNav]);

  const currentTours = regionData[activeRegion]?.categories[activeNav] || [];

  return (
    <section className="itinerary-navs">
      {/* Header */}
      <h5>browse by destination, type of travel or combine both.</h5>
      {/* Top Navigation (Categories) */}
      <div className="nav-list">
        {navItems.map((item) => (
          <button
            key={item}
            onClick={() => setActiveNav(item)}
            className={`nav-btn ${activeNav === item ? "active" : ""}`}
          >
            {item}
          </button>
        ))}
      </div>

      {/* Region Content */}
      <div className="region-navs">
        <div className="region-content">
          {/* Left: Map */}
          <div className="region-map">
            <BhutanMap
              activeRegion={activeRegion}
              onSelect={(region) => {
                if (regions.includes(region)) setActiveRegion(region);
              }}
            />
          </div>

          {/* Right: Region Tabs + Tours */}
          <div className="region-right">
            {/* Region Tabs */}
            <div className="region-tabs">
              {regions.map((region) => (
                <button
                  key={region}
                  onClick={() => setActiveRegion(region)}
                  className={`region-btn ${
                    activeRegion === region ? "active" : ""
                  }`}
                >
                  {region}
                </button>
              ))}
            </div>

            {/* Tours */}
            <div className="region-tours-wrapper">
              <button
                className={`scroll-arrow scroll-left ${
                  canScrollLeft ? "" : "hidden"
                }`}
                onClick={() => scrollTours("left")}
                aria-hidden={!canScrollLeft}
                tabIndex={canScrollLeft ? 0 : -1}
              >
                &larr;
              </button>

              <div className="region-tours" ref={toursRef}>
                {currentTours.length > 0 ? (
                  currentTours.map((tour, idx) => {
                    const tourRegion = tour.region ?? activeRegion;
                    const slug = slugify(`${tour.title}-${tourRegion}`);
                    return (
                      <Link
                        key={`${slug}-${idx}`}
                        to={`/itinerary/${slug}`}
                        state={{
                          tour,
                          region: tourRegion,
                          category: activeNav,
                          slug,
                        }}
                        className="tour-card-link"
                      >
                        <div
                          className={`tour-card ${
                            idx === 0 ? "active" : "faded"
                          }`}
                        >
                          <img src={tour.img} alt={tour.title} />
                          <h3>{tour.title}</h3>
                          <p>{tour.desc}</p>
                        </div>
                      </Link>
                    );
                  })
                ) : (
                  <p>
                    No tours available for <b>{activeNav}</b> in {activeRegion}.
                  </p>
                )}
              </div>

              <button
                className={`scroll-arrow scroll-right ${
                  canScrollRight ? "" : "hidden"
                }`}
                onClick={() => scrollTours("right")}
                aria-hidden={!canScrollRight}
                tabIndex={canScrollRight ? 0 : -1}
              >
                &rarr;
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
